import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { kDefaultFolderListLimit, kUserFilesFoldersApiBasePath } from '../constants'
import { FolderListResponse, FolderListResponseSchema } from '../schemas'

// Stopgap safety cap: keep automatic preloading under roughly 5,000 folders
// until the drive either virtualizes large lists (and this can expand) or
// switches to true UI pagination (and this limit can be removed).
const kMaxFolderChildrenPaginationPages = 10
const kFolderChildrenPaginationRequestDelayMs = 50

async function getFolderChildrenPage(params: {
  cursor?: string | null
  folderId: string
  limit: number
  pageIndex: number
  sentryTransactionId: string
  token: string
}): Promise<FolderListResponse> {
  const searchParams = new URLSearchParams({
    limit: String(params.limit),
  })
  if (params.cursor) {
    searchParams.set('cursor', params.cursor)
  }

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesFoldersApiBasePath}/${encodeURIComponent(params.folderId)}/children?${searchParams.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${params.token}`,
      'X-Transaction-Id': params.sentryTransactionId,
    },
  })

  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    const errorData = parseError ? {} : data
    const detail = formatUserFilesErrorDetail(errorData)

    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          responseData: errorData,
          parseError: parseError ? formatUnknownError(parseError) : null,
        },
      })
    })

    throw new ApiError(response.status, detail || `Failed to fetch folder children: ${response.status}`)
  }

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error('Folder children response JSON parse failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          parseError: formatUnknownError(parseError),
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid folder children response JSON')
  }

  const parsed = FolderListResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error('Folder children response schema validation failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          responseKeys: getTopLevelKeys(data),
          issues: parsed.error.issues,
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid folder children response shape', parsed.error.issues)
  }

  return parsed.data
}

/**
 * Get immediate child folders of a folder.
 *
 * @param folderId - The parent folder ID (UUID)
 * @param options.limit - Page size for backend pagination (1-500)
 * @returns FolderListResponse containing the full list of child folders
 * @throws Error when user is not authenticated
 * @throws ApiError when the folder-children request fails
 */
export async function getFolderChildren(folderId: string, options?: { limit?: number }): Promise<FolderListResponse> {
  const sentryTransactionId = nanoid()
  const limit = options?.limit ?? kDefaultFolderListLimit

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const allFolders: FolderListResponse['folders'] = []
    let nextCursor: string | null = null
    let parentId: string | null = null
    let pageIndex = 1
    const seenCursors = new Set<string>()

    do {
      if (pageIndex > kMaxFolderChildrenPaginationPages) {
        throw new Error(`Exceeded ${kMaxFolderChildrenPaginationPages} pages while fetching folder children`)
      }

      if (nextCursor) {
        if (seenCursors.has(nextCursor)) {
          throw new Error('Encountered a repeated pagination cursor while fetching folder children')
        }

        seenCursors.add(nextCursor)
      }

      const page = await getFolderChildrenPage({
        cursor: nextCursor,
        folderId,
        limit,
        pageIndex,
        sentryTransactionId,
        token,
      })

      allFolders.push(...page.folders)
      parentId = page.parent_id
      nextCursor = page.next_cursor
      pageIndex += 1

      if (nextCursor) {
        await new Promise((resolve) => setTimeout(resolve, kFolderChildrenPaginationRequestDelayMs))
      }
    } while (nextCursor)

    return {
      folders: allFolders,
      parent_id: parentId,
      next_cursor: null,
    }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: {
            folderId,
            limit,
          },
        })
      })
    }
    throw error
  }
}
